"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/jadwal-dokter/ - tempat berkas INI berada.
     ../components, ../i18n  -> naik satu tingkat ke app/
     ./dokter                -> tetangga di folder yang sama */
import Footer from "../components/Footer";
import { useLanguage } from "../i18n/LanguageProvider";
import { KOMPLAIN, PENDAFTARAN, PESAN_PENUTUP, PESAN_WA } from "./dokter";


const LEBAR_HALAMAN = 1240;

/* Berapa dokter yang ditampilkan per halaman.

   Angkanya sengaja ditaruh di sini, bukan disebar di dalam komponen.
   Mengubahnya di satu tempat langsung mengubah jumlah kartu, jumlah
   halaman, dan teks "Menampilkan 1-10 dari 51" sekaligus. */
const PER_HALAMAN = 10;

const NAMA_HARI = {
  id: ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
};

const WAJIB_JANJI = ["bedah-ortopedi"];

const TEKS = {
  eyebrow: { id: "Jadwal Praktik", en: "Practice Schedule" },
  judul: { id: "Jadwal Dokter", en: "Doctor Schedule" },
  sub: {
    id: "Cari dokter berdasarkan nama, spesialisasi, atau hari praktik. Jadwal dapat berubah sewaktu-waktu - mohon konfirmasi sebelum datang.",
    en: "Find a doctor by name, specialty, or practice day. Schedules may change - please confirm before visiting.",
  },

  pendaftaran: { id: "Info & Pendaftaran", en: "Registration" },
  komplain: { id: "Pengaduan layanan", en: "Service complaints" },
  daftarSekarang: { id: "Daftar lewat WhatsApp", en: "Register via WhatsApp" },
  telepon: { id: "Telepon", en: "Call" },
  panduanJudul: {
    id: "Cara mendaftar lewat WhatsApp",
    en: "How to register on WhatsApp",
  },
  panduanSub: {
    id: "Tujuh langkah, kurang dari satu menit.",
    en: "Seven steps, under a minute.",
  },
  panduan: [
    {
      judul: { id: "Cari dokternya", en: "Find the doctor" },
      isi: {
        id: "Ketik nama dokter di kolom pencarian, atau persempit lewat panel Cari Dokter dengan memilih spesialisasi dan hari praktik.",
        en: "Type the doctor's name in the search box, or narrow it down in the Find a Doctor panel by specialty and practice day.",
      },
    },
    {
      judul: { id: "Buka jadwal praktiknya", en: "Open the practice schedule" },
      isi: {
        id: "Tekan \u201cLihat jadwal\u201d di bawah nama poli pada kartu dokter, lalu pastikan hari dan jamnya sesuai dengan rencana Anda.",
        en: "Tap \u201cSee schedule\u201d below the clinic name on the doctor's card, then check that the day and time suit you.",
      },
    },
    {
      judul: { id: "Tekan tombol Daftar", en: "Tap the Register button" },
      isi: {
        id: "Tombol hijau di sisi kanan kartu dokter.",
        en: "The green button on the right-hand side of the doctor's card.",
      },
    },
    {
      judul: { id: "Baca pengingat yang muncul", en: "Read the reminder" },
      isi: {
        id: "Kotak pengingat menerangkan bahwa satu pesan sudah cukup. Pesan Anda pasti kami terima, jadi tidak perlu mengirim ulang.",
        en: "The reminder explains that one message is enough. Your message does reach us, so there is no need to send it again.",
      },
    },
    {
      judul: { id: "Tekan \u201cLanjut ke WhatsApp\u201d", en: "Tap \u201cContinue to WhatsApp\u201d" },
      isi: {
        id: "WhatsApp terbuka dengan pesan yang sudah terisi, lengkap dengan nama dokter yang Anda pilih.",
        en: "WhatsApp opens with a message already filled in, including the doctor you selected.",
      },
    },
    {
      judul: { id: "Kirim pesannya", en: "Send the message" },
      isi: {
        id: "Isinya masih boleh Anda sunting lebih dulu - misalnya menambahkan hari yang Anda inginkan.",
        en: "You can still edit it first - for example to add the day you would prefer.",
      },
    },
    {
      judul: { id: "Tunggu balasan kami", en: "Wait for our reply" },
      isi: {
        id: "Petugas resepsionis membalas satu per satu sesuai urutan pesan masuk. Mohon berkenan menunggu.",
        en: "Our receptionists reply to each message in the order it arrives. Thank you for your patience.",
      },
    },
  ],

  cariDokter: { id: "Cari Dokter", en: "Find a Doctor" },
  cariNama: { id: "Nama dokter", en: "Doctor name" },
  cariSpesialisasi: { id: "Pilih spesialisasi", en: "Search specialty" },
  tidakAdaSpesialisasi: { id: "Tidak ada yang cocok", en: "No matches" },
  lihatJadwal: { id: "Lihat jadwal", en: "See schedule" },
  tutupJadwal: { id: "Tutup jadwal", en: "Hide schedule" },
  cariPlaceholder: { id: "Ketik nama dokter", en: "Type a doctor's name" },
  spesialisasi: { id: "Spesialisasi", en: "Specialty" },
  semuaSpesialisasi: { id: "Semua spesialisasi", en: "All specialties" },
  hari: { id: "Hari praktik", en: "Practice day" },
  semuaHari: { id: "Semua hari", en: "Any day" },
  bersihkan: { id: "Bersihkan penyaring", en: "Clear filters" },

  hasil: { id: "dokter ditemukan", en: "doctors found" },

  /* ---------- PAGINASI ---------- */
  menampilkan: { id: "Menampilkan", en: "Showing" },
  dari: { id: "dari", en: "of" },
  navHalaman: { id: "Navigasi halaman", en: "Pagination" },
  halamanSebelumnya: { id: "Halaman sebelumnya", en: "Previous page" },
  halamanBerikutnya: { id: "Halaman berikutnya", en: "Next page" },
  keHalaman: { id: "Ke halaman", en: "Go to page" },
  jadwalPraktik: { id: "Jadwal praktik", en: "Practice schedule" },

  wajibJanjiJudul: {
    id: "Wajib membuat janji terlebih dahulu",
    en: "Appointment required before visiting",
  },
  wajibJanjiIsi: {
    id: "Pasien poli ini harus membuat janji sebelum datang ke rumah sakit. Konfirmasikan jadwal Anda ke customer service lewat WhatsApp.",
    en: "Patients of this clinic must book before coming to the hospital. Please confirm your slot with customer service on WhatsApp.",
  },
  wajibJanjiTombol: { id: "Konfirmasi via WhatsApp", en: "Confirm on WhatsApp" },

  /* ⚠️ `bukaTabBaru` DICABUT DARI PEMAKAIAN.

     Kunci itu dulu dibacakan pembaca layar sebagai "(terbuka di tab
     baru)" pada tautan WhatsApp di blok Wajib Janji. Tombol itu kini
     membuka DIALOG lebih dulu, bukan tab baru - jadi keterangannya
     jadi salah, dan keterangan salah untuk pembaca layar lebih buruk
     daripada tidak ada keterangan sama sekali. */
  bukaTabBaru: { id: "(terbuka di tab baru)", en: "(opens in a new tab)" },

  daftar: { id: "Daftar", en: "Register" },

  /* ---------- DIALOG SEBELUM MENUJU WHATSAPP ---------- */
  dialogUntuk: { id: "Pendaftaran ke", en: "Registering with" },
  dialogJudul: { id: "Mohon diperhatikan sebelum melanjutkan", en: "Please read before continuing" },
  dialogIsi: {
    id: "Sebentar lagi Anda akan terhubung dengan petugas pendaftaran kami di WhatsApp. Pesan Anda pasti kami terima, dan akan dibalas satu per satu sesuai urutan masuk - mohon berkenan menunggu balasan dari kami.",
    en: "You are about to be connected with our registration staff on WhatsApp. Your message will reach us, and we reply to every message in the order it arrives - we appreciate your patience while you wait.",
  },
  dialogTerima: {
    id: "Terima kasih telah mempercayakan kesehatan Anda kepada RS Jakarta Medical Center.",
    en: "Thank you for trusting RS Jakarta Medical Center with your care.",
  },
  /* ⚠️ KALIMAT YANG PALING BERPENGARUH BELUM ADA DI SINI.

     Orang mengirim pesan kedua berjam-jam setelah membaca dialog ini -
     saat cemas belum dibalas, bukan saat menekan tombol. Yang
     benar-benar menghentikannya adalah EKSPEKTASI WAKTU, misalnya
     "Balasan biasanya kami kirim dalam 1x24 jam pada hari kerja."

     Saya tidak menuliskannya karena saya tidak tahu angka
     sebenarnya, dan mengarangnya akan menghasilkan janji yang
     dilanggar rumah sakit Anda sendiri. Tanyakan ke bagian
     pendaftaran, lalu sisipkan di akhir dialogIsi. */
  dialogOke: { id: "Lanjut ke WhatsApp", en: "Continue to WhatsApp" },
  dialogBatal: { id: "Tutup", en: "Close" },
  dialogTutup: { id: "Tutup", en: "Close" },

  belumAda: { id: "Jadwal dokter sedang disiapkan", en: "The doctor schedule is being prepared" },
  belumAdaPetunjuk: {
    id: "Untuk sementara, silakan hubungi nomor pendaftaran di atas untuk menanyakan jadwal dokter yang Anda cari.",
    en: "In the meantime, please contact the registration numbers above to ask about the schedule you need.",
  },
  kosong: { id: "Tidak ada dokter yang cocok", en: "No matching doctors" },
  kosongPetunjuk: {
    id: "Coba ubah kata kunci, atau longgarkan penyaring spesialisasi dan harinya.",
    en: "Try different keywords, or loosen the specialty and day filters.",
  },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

/* Inisial dari nama dokter, untuk kartu yang belum berfoto. Gelar di
   depan ("dr.", "drg.") dan di belakang ("Sp.PD") dibuang dulu -
   kalau ikut, yang muncul "DS" dari "dr. Sp..." dan itu sama saja
   untuk semua orang. */
function inisial(nama) {
  const bersih = nama
    .replace(/^(dr\.|drg\.|prof\.|dr|drg)\s*/gi, "")
    .split(",")[0]
    .trim();

  const kata = bersih.split(/\s+/).filter(Boolean);
  return (kata[0]?.[0] ?? "") + (kata[1]?.[0] ?? "");
}

function jamTampil(mulai, selesai) {
  return `${mulai.replace(":", ".")} - ${selesai.replace(":", ".")}`;
}

/* ============================================================
   PEMETAAN DARI PANEL "CARI DOKTER" DI BERANDA

   DoctorSearch.jsx mengirim pengunjung ke sini dengan query param:

     /jadwal-dokter?nama=budi&spesialisasi=penyakit-dalam&hari=senin

   Dua kosakata perlu diterjemahkan, dan keduanya berbeda dari yang
   dipakai di halaman ini:

     hari          "senin" (teks)     -> 1 (angka, seperti di data)
     spesialisasi  "penyakit-dalam"   -> dicocokkan ke d.filter

   Untuk spesialisasi, TIDAK bisa dicocokkan ke nama tampilannya
   ("Penyakit Dalam"), karena nama itu berubah mengikuti bahasa
   sementara slug di URL tidak.
   ============================================================ */
const SLUG_HARI = {
  minggu: "0",
  senin: "1",
  selasa: "2",
  rabu: "3",
  kamis: "4",
  jumat: "5",
  sabtu: "6",
};

export default function JadwalDokterContent({ daftarDokter = [], waktuTarik = null }) {
  const { lang, t } = useLanguage();

  const [kata, setKata] = useState("");
  const [cariSpesialisasi, setCariSpesialisasi] = useState("");
  const [spesialisasiDipilih, setSpesialisasiDipilih] = useState([]);
  const [hari, setHari] = useState("");
  const [slugSpesialisasi, setSlugSpesialisasi] = useState("");
  const [jadwalTerbuka, setJadwalTerbuka] = useState([]);

  /* Halaman yang sedang dibuka. 1 = halaman pertama, bukan 0 - angka
     ini tampil apa adanya di tombol, jadi menyimpannya berbasis nol
     hanya menambah satu konversi yang gampang terlewat. */
  const [halaman, setHalaman] = useState(1);

  /* Dialog konfirmasi sebelum membuka WhatsApp. null = tertutup;
     kalau terisi, isinya { nama, href } dokter yang sedang didaftar. */
  const [konfirmasi, setKonfirmasi] = useState(null);

  /* Tombol yang membuka dialognya, supaya fokus bisa dikembalikan ke
     sana saat ditutup. Tanpa ini, pengguna papan tik terlempar ke awal
     halaman dan harus menekan Tab puluhan kali untuk kembali ke dokter
     yang tadi. */
  const pemicuRef = useRef(null);

  /* Nilai dari URL dipakai sebagai NILAI AWAL saja, lalu penyaring
     sepenuhnya dikendalikan pengunjung. Sengaja tidak disinkronkan
     dua arah: kalau setiap centang ikut mengubah URL, tombol Back
     peramban jadi menelusuri riwayat penyaringan alih-alih kembali
     ke halaman sebelumnya - perilaku yang membingungkan. */
  const searchParams = useSearchParams();

  /* ============================================================
     MEMBACA PENYARING DARI URL

     ⚠️ SENGAJA BUKAN useEffect. Dulu blok ini berbentuk:

         useEffect(() => { ...setKata/setHari/setSlug... },
                   [searchParams]);

     dan ditolak aturan react-hooks/set-state-in-effect. Bukan
     rewel: dengan useEffect, pengunjung yang datang dari panel Cari
     Dokter di beranda menerima satu render berisi daftar LENGKAP
     lebih dulu - 53 dokter, tanpa penyaring - yang terpasang ke DOM,
     baru kemudian efeknya jalan dan mempersempitnya. Di jaringan
     lambat, kedipan itu benar-benar terlihat.

     Pola di bawah menyelesaikannya sebelum apa pun sampai ke layar:
     React membuang render yang sedang berjalan dan mengulanginya
     dengan nilai yang sudah benar.

     PERILAKUNYA TIDAK BERUBAH. Pembandingnya `searchParams
     .toString()`, jadi blok ini tetap jalan ulang saat URL berubah
     tanpa komponen dimuat ulang - persis seperti dependensi
     [searchParams] dulu. Malah sedikit lebih ketat: acuan objek yang
     baru dengan query yang sama tidak lagi memicu apa-apa.

     tandaUrlSebelumnya dimulai dari null, bukan dari tandaUrl,
     supaya blok ini ikut jalan pada render pertama - meniru
     useEffect yang dulu jalan saat komponen lahir.
     ============================================================ */
  const tandaUrl = searchParams.toString();
  const [tandaUrlSebelumnya, setTandaUrlSebelumnya] = useState(null);

  if (tandaUrl !== tandaUrlSebelumnya) {
    setTandaUrlSebelumnya(tandaUrl);

    const nama = searchParams.get("nama");
    if (nama) setKata(nama);

    const h = searchParams.get("hari");
    if (h && SLUG_HARI[h.toLowerCase()]) setHari(SLUG_HARI[h.toLowerCase()]);

    /* Slug spesialisasi disimpan apa adanya. Pencocokannya terjadi di
       `hasil` di bawah, terhadap d.filter - bukan terhadap nama
       tampilan, yang berubah mengikuti bahasa. */
    const sp = searchParams.get("spesialisasi");
    if (sp) setSlugSpesialisasi(sp.toLowerCase());
  }

  const namaHari = NAMA_HARI[lang === "en" ? "en" : "id"];

  /* Daftar spesialisasi disusun dari data dan diurutkan menurut abjad
     bahasa yang aktif. Ditulis manual berisiko ketinggalan setiap kali
     ada dokter baru. */
  const daftarSpesialisasi = useMemo(() => {
    const kumpulan = new Set();
    daftarDokter.forEach((d) => {
      const nama = pilihTeks(d.spesialisasi, lang);
      if (nama) kumpulan.add(nama);
    });
    return [...kumpulan].sort((a, b) => a.localeCompare(b, lang === "en" ? "en" : "id"));
  }, [daftarDokter, lang]);

  /* Kotak pencarian kecil di atas daftar spesialisasi. Dengan puluhan
     spesialisasi, menggulir daftar centang lebih lambat daripada
     mengetik tiga huruf. */
  const spesialisasiTampil = useMemo(() => {
    const q = cariSpesialisasi.trim().toLowerCase();
    if (!q) return daftarSpesialisasi;
    return daftarSpesialisasi.filter((s) => s.toLowerCase().includes(q));
  }, [cariSpesialisasi, daftarSpesialisasi]);

  const hasil = useMemo(() => {
    const q = kata.trim().toLowerCase();

    return daftarDokter.filter((d) => {
      if (q && !d.nama.toLowerCase().includes(q)) return false;
      if (spesialisasiDipilih.length > 0 && !spesialisasiDipilih.includes(pilihTeks(d.spesialisasi, lang))) {
        return false;
      }
      /* Penyaring dari URL. Hanya berlaku selama pengunjung belum
         menyentuh daftar centang - begitu ia mencentang sendiri,
         pilihannyalah yang dipakai. */
      if (slugSpesialisasi && spesialisasiDipilih.length === 0 && d.filter !== slugSpesialisasi) {
        return false;
      }
      if (hari !== "" && !d.jadwal?.some((j) => String(j.hari) === hari)) return false;
      return true;
    });
  }, [daftarDokter, kata, spesialisasiDipilih, slugSpesialisasi, hari, lang]);

  const menyaring = kata.trim() !== "" || spesialisasiDipilih.length > 0 || hari !== "";

  /* ============================================================
     PAGINASI

     Dihitung dari `hasil` - yaitu dokter SETELAH disaring - bukan
     dari daftarDokter. Kalau dihitung dari daftar penuh, mencentang
     "Gigi" yang hanya berisi 4 dokter tetap memunculkan 6 tombol
     halaman, dan halaman 2 sampai 6 kosong melompong.

     `halamanAman` menjepit nilainya ke rentang yang masih ada.
     Kasusnya nyata: pengunjung membuka halaman 5, lalu mengetik nama
     dokter sehingga hasilnya tinggal 3. Tanpa penjepit ini, slice-nya
     jatuh di luar larik dan yang tampil adalah daftar kosong padahal
     dokternya ketemu.

     Penjepitan dilakukan SAAT RENDER, bukan lewat useEffect, supaya
     tidak ada satu frame kosong yang sempat terlihat lebih dulu.
     ============================================================ */
  const totalHalaman = Math.max(1, Math.ceil(hasil.length / PER_HALAMAN));
  const halamanAman = Math.min(halaman, totalHalaman);
  const mulai = (halamanAman - 1) * PER_HALAMAN;
  const hasilHalaman = hasil.slice(mulai, mulai + PER_HALAMAN);

  /* ============================================================
     KEMBALI KE HALAMAN 1 SETIAP KALI PENYARINGNYA BERUBAH

     Beda dengan penjepit di atas, yang ini soal maksud pengunjung:
     orang yang baru saja mencentang spesialisasi ingin melihat hasil
     dari awal, bukan mendarat di tengah-tengah daftar barunya.

     ⚠️ SENGAJA BUKAN useEffect.

     Versi pertama menulis ini sebagai:

         useEffect(() => { setHalaman(1); },
                   [kata, spesialisasiDipilih, hari, slugSpesialisasi]);

     Itu ditolak aturan react-hooks/set-state-in-effect, dan
     penolakannya benar. Dengan useEffect, urutannya jadi: React
     merender daftar memakai halaman LAMA, memasangnya ke DOM,
     BARULAH efeknya jalan dan memicu render kedua. Untuk sesaat,
     hasil penyaringan yang baru ditampilkan dengan potongan halaman
     yang lama.

     Pola di bawah ini yang dianjurkan React untuk kasus "satu state
     perlu ikut berubah saat state lain berubah": bandingkan saat
     render, lalu setState di sana juga. React membuang hasil render
     yang sedang berjalan dan mengulanginya SEBELUM apa pun sampai ke
     layar - jadi tidak ada render yang sia-sia terpasang, dan tidak
     ada kedipan.

     Menekan aturannya dengan eslint-disable akan menghasilkan kode
     yang lolos lint tapi tetap punya render bertingkat itu.

     tandaSaring sengaja sebuah string, bukan larik pembanding.
     spesialisasiDipilih adalah larik yang acuannya berubah tiap
     centang, jadi membandingkan acuannya akan selalu "berbeda" dan
     memaksa reset di tiap render.
     ============================================================ */
  const tandaSaring = JSON.stringify([kata, spesialisasiDipilih, hari, slugSpesialisasi]);
  const [tandaSaringSebelumnya, setTandaSaringSebelumnya] = useState(tandaSaring);

  if (tandaSaring !== tandaSaringSebelumnya) {
    setTandaSaringSebelumnya(tandaSaring);
    setHalaman(1);
  }

  /* Titik yang digulir saat pindah halaman. Tanpa ini, menekan "2" di
     dasar halaman membuat pengunjung tetap berada di dasar - melihat
     tombol paginasi dan footer, sementara sepuluh dokter barunya ada
     jauh di atas layar. */
  const kotakHasilRef = useRef(null);

  function pindahHalaman(n) {
    setHalaman(n);

    const kotak = kotakHasilRef.current;
    if (!kotak) return;

    /* 120px disisakan untuk header yang menempel di atas layar.
       Tanpa selisih itu, judul "51 dokter ditemukan" tertutup header. */
    const atas = kotak.getBoundingClientRect().top + window.scrollY - 120;

    const diamSaja =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    window.scrollTo({ top: Math.max(0, atas), behavior: diamSaja ? "auto" : "smooth" });
  }

  function ubahSpesialisasi(nama) {
    setSpesialisasiDipilih((sebelumnya) =>
      sebelumnya.includes(nama) ? sebelumnya.filter((s) => s !== nama) : [...sebelumnya, nama]
    );
  }

  function ubahJadwal(id) {
    setJadwalTerbuka((sebelumnya) =>
      sebelumnya.includes(id) ? sebelumnya.filter((x) => x !== id) : [...sebelumnya, id]
    );
  }

  function bersihkan() {
    setKata("");
    setCariSpesialisasi("");
    setSpesialisasiDipilih([]);
    setHari("");

    /* Wajib ikut dikosongkan. Tanpa ini, tombol "Bersihkan" akan
       terlihat tidak bekerja bagi pengunjung yang datang dari panel
       Cari Dokter di beranda: penyaring dari URL tetap aktif dan
       hasilnya tidak kembali ke seluruh dokter. */
    setSlugSpesialisasi("");
  }

  /* ---------- ALUR DIALOG ---------- */

  function mintaDaftar(data, pemicu) {
    pemicuRef.current = pemicu ?? null;
    setKonfirmasi(data);
  }

  function tutupKonfirmasi() {
    setKonfirmasi(null);
    pemicuRef.current?.focus();
  }

  function lanjutKeWhatsApp() {
    const href = konfirmasi?.href;
    setKonfirmasi(null);

    /* window.open DI DALAM penangan klik tombol Oke, bukan di efek
       setelahnya. Penekanan tombol itu sendiri adalah sentuhan
       langsung pengguna, jadi peramban ponsel mengizinkannya. Kalau
       dipindah ke useEffect, ia kehilangan kaitan dengan sentuhan dan
       Safari iOS memblokirnya sebagai popup - tanpa pesan apa pun. */
    if (href) window.open(href, "_blank", "noopener,noreferrer");
    pemicuRef.current?.focus();
  }

  const gayaKolom = {
    width: "100%",
    borderRadius: 10,
    border: "1px solid var(--c-line)",
    backgroundColor: "var(--c-surface)",
    padding: "11px 12px",
    fontSize: 14.5,
    outline: "none",
  };

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 16px" }}>
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA ----------
         Nomor pendaftaran ditaruh di kepala, bukan di dasar: bahkan
         setelah daftar dokternya lengkap, pendaftaran tetap lewat
         telepon - jadi nomor itu bagian dari jawaban, bukan lampiran. */}
      <div className="bg-[var(--c-tint)]">
        <div style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "44px 16px 40px" }}>
          <p className="font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]" style={{ fontSize: 11.5 }}>
            {pilihTeks(TEKS.eyebrow, lang)}
          </p>

          <h1 className="text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ marginTop: 10, fontSize: "clamp(30px, 4.8vw, 44px)" }}>
            {pilihTeks(TEKS.judul, lang)}
          </h1>
          {waktuTarik && (
            <p className="text-[var(--c-muted)]" style={{ marginTop: 8, fontSize: 12 }}>
              {lang === "en" ? "Updated" : "Diperbarui"}{" "}
              {new Date(waktuTarik).toLocaleString(lang === "en" ? "en-GB" : "id-ID", {
                timeZone: "Asia/Jakarta",
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </p>
          )}

          <p className="text-[var(--c-body)]" style={{ marginTop: 14, maxWidth: 640, fontSize: 15.5, lineHeight: 1.7 }}>
            {pilihTeks(TEKS.sub, lang)}
          </p>

          <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-muted)]" style={{ marginTop: 24, fontSize: 10.5 }}>
            {pilihTeks(TEKS.pendaftaran, lang)}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: 14, marginTop: 12, maxWidth: 720 }}>
            {PENDAFTARAN.map((n) => (
              <article key={n.kunci} className="border border-[var(--c-brand-line)] bg-[var(--c-surface)]" style={{ borderRadius: 12, padding: "16px 18px" }}>
                <p className="font-semibold text-[var(--c-muted)]" style={{ fontSize: 12.5 }}>
                  {pilihTeks(n.label, lang)}
                </p>

                <p style={{ marginTop: 6 }}>
                  <a href={`tel:${n.tel}`} className="font-bold text-[var(--c-ink)] transition-colors hover:text-[var(--c-brand)]" style={{ fontSize: 21 }}>
                    {n.tampil}
                  </a>
                </p>

                {/* ⚠️ TAUTAN INI SENGAJA TIDAK LEWAT DIALOG.

                    Dialognya menyebut nama dokter, dan di sini belum
                    ada dokter yang dipilih - pesannya cuma "saya ingin
                    mendaftar". Memaksakan dialog di sini akan
                    menampilkan kotak tanpa isi yang berarti. */}
                <a
                  href={`https://wa.me/${n.wa}?text=${encodeURIComponent(`${pilihTeks(PESAN_WA, lang)}\n\n${pilihTeks(PESAN_PENUTUP, lang)}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-bold text-[var(--c-brand)] underline underline-offset-4 transition-colors hover:text-[var(--c-ink)]"
                  style={{ marginTop: 10, fontSize: 13 }}
                >
                  <IkonWhatsApp />
                  {pilihTeks(TEKS.daftarSekarang, lang)}
                </a>
              </article>
            ))}
          </div>

          {/* ---------- PANDUAN TUJUH LANGKAH ----------

             ⚠️ <details>, BUKAN useState.

             Isinya statis dan tidak berhubungan dengan state lain di
             halaman ini. <details> sudah membawa perilaku buka-tutup,
             sudah bisa dijangkau papan ketik, sudah diumumkan pembaca
             layar sebagai grup yang bisa dilipat, dan tetap bekerja
             kalau JavaScript gagal dimuat. Menuliskannya dengan
             useState berarti membangun ulang semuanya lalu
             melupakan sebagiannya.

             ⚠️ TERTUTUP SECARA BAWAAN, dan itu keputusan sadar.
             Kepala halaman ini sudah memuat judul, keterangan, dan
             dua kartu nomor; tujuh langkah yang terbuka akan
             mendorong pencarian dokter - alasan utama orang membuka
             halaman ini - jauh ke bawah lipatan layar. Yang butuh
             panduan akan membukanya; yang sudah tahu tidak
             terhalang.

             ⚠️ DITARUH DI SINI, BUKAN DI DASAR HALAMAN. Tepat di
             bawah dua kartu nomor, di titik orang sedang memutuskan
             cara menghubungi. Panduan yang baru ditemukan setelah
             seluruh daftar dokter terlewati datang terlambat.

             ⚠️ PENANDA SEGITIGA BAWAANNYA DIBIARKAN. Menggantinya
             dengan ikon sendiri menuntut varian [&[open]] yang
             beberapa kali tidak terbentuk di proyek ini - dan
             gagalnya diam-diam: penandanya hilang, tidak ada yang
             menggantikan, dan tidak ada galat apa pun. */}
          <details
            className="border border-[var(--c-brand-line)] bg-[var(--c-surface)]"
            style={{ marginTop: 14, maxWidth: 720, borderRadius: 12, padding: "14px 18px" }}
          >
            <summary
              className="cursor-pointer font-bold text-[var(--c-ink)] transition-colors hover:text-[var(--c-brand)]"
              style={{ fontSize: 14.5 }}
            >
              {pilihTeks(TEKS.panduanJudul, lang)}
            </summary>

            <p className="text-[var(--c-muted)]" style={{ marginTop: 6, fontSize: 12.5 }}>
              {pilihTeks(TEKS.panduanSub, lang)}
            </p>

            {/* Nomor digambar sendiri sebagai lingkaran, bukan memakai
               penomoran bawaan <ol>. Angka bawaan duduk di luar kotak
               teks dan ikut mengecil bersama hurufnya; di panduan
               langkah, nomor justru yang pertama dicari mata saat
               orang kembali ke halaman ini setelah sempat beralih ke
               WhatsApp.

               Penomorannya tetap dari <ol>, jadi pembaca layar tetap
               mengumumkannya sebagai daftar berurut. */}
            <ol style={{ marginTop: 14, display: "grid", gap: 14 }}>
              {TEKS.panduan.map((langkah, i) => (
                <li key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span
                    aria-hidden="true"
                    className="shrink-0 bg-[var(--c-brand)] font-bold text-white"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      fontSize: 12,
                      marginTop: 1,
                    }}
                  >
                    {i + 1}
                  </span>

                  <span style={{ minWidth: 0 }}>
                    <span className="block font-bold text-[var(--c-ink)]" style={{ fontSize: 13.5 }}>
                      {pilihTeks(langkah.judul, lang)}
                    </span>
                    <span className="block text-[var(--c-body)]" style={{ marginTop: 2, fontSize: 13, lineHeight: 1.65 }}>
                      {pilihTeks(langkah.isi, lang)}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </details>
        </div>
      </div>

      <section style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "32px 16px 64px" }}>

        {daftarDokter.length === 0 ? (
          <Kosong
            judul={pilihTeks(TEKS.belumAda, lang)}
            petunjuk={pilihTeks(TEKS.belumAdaPetunjuk, lang)}
          />
        ) : (
          /* ---------- DUA KOLOM: PENYARING + HASIL ----------
             minmax(0, 1fr) pada kolom kedua, bukan 1fr saja: tanpa itu
             kolom grid menolak menyusut di bawah lebar isinya, dan
             nama dokter yang panjang mendorong seluruh tata letak
             melebihi layar. */
          <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">

            {/* ---------- PENYARING ----------
               `lg:sticky` supaya penyaring tetap terlihat saat daftar
               panjang digulir. Di ponsel tidak lengket - kalau lengket,
               ia memakan sebagian besar layar. */}
            <aside className="lg:sticky lg:top-[130px] lg:self-start">
              <div className="border border-[var(--c-line)]" style={{ borderRadius: 14, padding: "20px 18px" }}>
                <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 17 }}>
                  {pilihTeks(TEKS.cariDokter, lang)}
                </p>

                {/* --- hari --- */}
                <div style={{ marginTop: 18 }}>
                  <label htmlFor="pilih-hari" className="font-semibold text-[var(--c-body)]" style={{ fontSize: 13 }}>
                    {pilihTeks(TEKS.hari, lang)}
                  </label>
                  <select
                    id="pilih-hari"
                    value={hari}
                    onChange={(e) => setHari(e.target.value)}
                    className="text-[var(--c-ink)]"
                    style={{ ...gayaKolom, marginTop: 6 }}
                  >
                    <option value="">{pilihTeks(TEKS.semuaHari, lang)}</option>
                    {namaHari.map((h, i) => (
                      <option key={h} value={String(i)}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>

                {/* --- spesialisasi --- */}
                <div className="border-t border-[var(--c-line)]" style={{ marginTop: 20, paddingTop: 18 }}>
                  <p className="font-semibold text-[var(--c-body)]" style={{ fontSize: 13 }}>
                    {pilihTeks(TEKS.spesialisasi, lang)}
                  </p>

                  <div style={{ position: "relative", marginTop: 8 }}>
                    <label htmlFor="cari-spesialisasi" className="sr-only">
                      {pilihTeks(TEKS.cariSpesialisasi, lang)}
                    </label>
                    <IkonCari />
                    <input
                      id="cari-spesialisasi"
                      type="search"
                      value={cariSpesialisasi}
                      onChange={(e) => setCariSpesialisasi(e.target.value)}
                      placeholder={pilihTeks(TEKS.cariSpesialisasi, lang)}
                      className="text-[var(--c-ink)] placeholder:text-[var(--c-faint)]"
                      style={{ ...gayaKolom, paddingLeft: 38, fontSize: 13.5 }}
                    />
                  </div>

                  {/* Daftar centang bergulir sendiri kalau isinya
                     banyak, supaya tombol di bawahnya tidak terdorong
                     jauh keluar layar. */}
                  <div style={{ marginTop: 10, maxHeight: 260, overflowY: "auto", display: "grid", gap: 2 }}>
                    {spesialisasiTampil.length === 0 ? (
                      <p className="text-[var(--c-muted)]" style={{ padding: "8px 2px", fontSize: 13 }}>
                        {pilihTeks(TEKS.tidakAdaSpesialisasi, lang)}
                      </p>
                    ) : (
                      spesialisasiTampil.map((sp) => {
                        const aktif = spesialisasiDipilih.includes(sp);

                        return (
                          <label
                            key={sp}
                            className={aktif ? "bg-[var(--c-tint)]" : "hover:bg-[var(--c-surface-2)]"}
                            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", borderRadius: 8, padding: "8px 8px" }}
                          >
                            <input
                              type="checkbox"
                              checked={aktif}
                              onChange={() => ubahSpesialisasi(sp)}
                              className="accent-[var(--c-brand)]"
                              style={{ width: 15, height: 15, flex: "0 0 auto" }}
                            />
                            <span className={aktif ? "font-semibold text-[var(--c-ink)]" : "text-[var(--c-body)]"} style={{ fontSize: 13.5, lineHeight: 1.4 }}>
                              {sp}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                {menyaring && (
                  <button
                    type="button"
                    onClick={bersihkan}
                    className="w-full border-t border-[var(--c-line)] font-bold text-[var(--c-brand)] transition-colors hover:text-[var(--c-ink)]"
                    style={{ marginTop: 16, paddingTop: 14, fontSize: 13.5 }}
                  >
                    {pilihTeks(TEKS.bersihkan, lang)}
                  </button>
                )}
              </div>
            </aside>

            {/* ---------- HASIL ---------- */}
            <div ref={kotakHasilRef} style={{ minWidth: 0 }}>
              {/* aria-live: pembaca layar mengumumkan jumlah hasilnya
                 saat penyaring diubah. Tanpa itu, pengguna mengetik
                 lalu tidak tahu apa yang berubah di bawah. */}
              <h2 aria-live="polite" className="font-bold text-[var(--c-ink)]" style={{ fontSize: 22 }}>
                {hasil.length} {pilihTeks(TEKS.hasil, lang)}
              </h2>

              <div style={{ position: "relative", marginTop: 14 }}>
                <label htmlFor="cari-dokter" className="sr-only">
                  {pilihTeks(TEKS.cariNama, lang)}
                </label>
                <IkonCari />
                <input
                  id="cari-dokter"
                  type="search"
                  value={kata}
                  onChange={(e) => setKata(e.target.value)}
                  placeholder={pilihTeks(TEKS.cariPlaceholder, lang)}
                  className="text-[var(--c-ink)] placeholder:text-[var(--c-faint)]"
                  style={{ ...gayaKolom, paddingLeft: 42, padding: "14px 14px 14px 42px", fontSize: 15 }}
                />
              </div>

              {hasil.length === 0 ? (
                <Kosong
                  judul={pilihTeks(TEKS.kosong, lang)}
                  petunjuk={pilihTeks(TEKS.kosongPetunjuk, lang)}
                />
              ) : (
                <>
                  {/* Keterangan posisi. Judul di atas hanya menyebut
                     jumlah total; tanpa baris ini pengunjung yang
                     melihat 10 kartu di bawah judul "51 dokter
                     ditemukan" wajar mengira 41 sisanya hilang. */}
                  {totalHalaman > 1 && (
                    <p
                      className="text-[var(--c-faint)]"
                      style={{ fontSize: 13, marginTop: 14 }}
                    >
                      {pilihTeks(TEKS.menampilkan, lang)} {mulai + 1}-{mulai + hasilHalaman.length}{" "}
                      {pilihTeks(TEKS.dari, lang)} {hasil.length}
                    </p>
                  )}

                  <div style={{ display: "grid", gap: 14, marginTop: 18 }}>
                    {hasilHalaman.map((d) => (
                      <BarisDokter
                        key={d.id}
                        dokter={d}
                        lang={lang}
                        namaHari={namaHari}
                        hariTerpilih={hari}
                        terbuka={jadwalTerbuka.includes(d.id)}
                        onToggle={() => ubahJadwal(d.id)}
                        onDaftar={mintaDaftar}
                      />
                    ))}
                  </div>

                  <Paginasi
                    halaman={halamanAman}
                    total={totalHalaman}
                    lang={lang}
                    onPindah={pindahHalaman}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* ---------- KOMPLAIN ----------
           Paling bawah dan kecil: penting, tapi bukan yang dicari orang
           saat membuka halaman jadwal dokter. */}
        {KOMPLAIN.length > 0 && (
          <div className="border-t border-[var(--c-line)]" style={{ marginTop: 48, paddingTop: 20 }}>
            <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-faint)]" style={{ fontSize: 10.5 }}>
              {pilihTeks(TEKS.komplain, lang)}
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
              {KOMPLAIN.map((n) => (
                <a
                  key={n.tel}
                  href={`https://wa.me/${n.wa}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-bold text-[var(--c-body)] transition-colors hover:text-[var(--c-brand)]"
                  style={{ fontSize: 14 }}
                >
                  <IkonWhatsApp />
                  {n.tampil}
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      {konfirmasi && (
        <DialogSatuPesan
          lang={lang}
          nama={konfirmasi.nama}
          onBatal={tutupKonfirmasi}
          onLanjut={lanjutKeWhatsApp}
        />
      )}

      <Footer />
    </div>
  );
}

/* ============================================================
   BARIS DOKTER - memanjang, bukan kartu grid

   Bentuk memanjang dipilih karena yang dibandingkan orang di halaman
   ini adalah NAMA dan SPESIALISASI - keduanya teks, dan teks lebih
   cepat dipindai kalau tersusun ke bawah dalam satu kolom lurus
   daripada tersebar di kisi.

   Jadwalnya disembunyikan di balik "Lihat jadwal" dan dibuka per
   dokter. Dengan puluhan dokter, jadwal yang selalu terbuka membuat
   daftarnya berkali-kali lebih panjang, dan orang yang sedang mencari
   nama harus menggulir melewati jadwal yang tidak dia butuhkan.

   Kalau hari tertentu sedang dipilih di penyaring, jadwalnya TERBUKA
   SENDIRI dan baris hari itu ditandai - karena di situlah orang
   memang sedang mencari jam.
   ============================================================ */

function BarisDokter({ dokter, lang, namaHari, hariTerpilih, terbuka, onToggle, onDaftar }) {
  const jadwal = [...(dokter.jadwal ?? [])].sort(
    (a, b) => a.hari - b.hari || a.mulai.localeCompare(b.mulai)
  );

  /* Menyaring per hari berarti orangnya sedang mencari jam praktik,
     jadi jadwalnya dibuka tanpa perlu ditekan. */
  const tampilkanJadwal = terbuka || hariTerpilih !== "";

  const pesan = [
    pilihTeks(PESAN_WA, lang),
    "",
    `${lang === "en" ? "Doctor" : "Dokter"}: ${dokter.nama}`,
    `${lang === "en" ? "Specialty" : "Spesialisasi"}: ${pilihTeks(dokter.spesialisasi, lang)}`,
    "",
    pilihTeks(PESAN_PENUTUP, lang),
  ].join("\n");

  const href = `https://wa.me/${PENDAFTARAN[0].wa}?text=${encodeURIComponent(pesan)}`;
  const idJadwal = `jadwal-${dokter.id}`;

  /* Dicocokkan ke slug, bukan nama tampilan - lihat catatan di
     WAJIB_JANJI. Dokter dari poli yang tidak terpetakan bisa punya
     filter null; includes(null) aman, hasilnya false. */
  const wajibJanji = WAJIB_JANJI.includes(dokter.filter);

  /* RINGKASAN HARI PRAKTIK.

     Dihitung dari jadwalnya sendiri, bukan ditulis manual. Gunanya
     supaya pengunjung tahu seberapa sering dokter ini praktik TANPA
     harus membuka jadwalnya lebih dulu.

     Satu dokter bisa punya dua sesi di hari yang sama (pagi dan
     sore), jadi harinya dihitung unik lewat Set - kalau tidak,
     "3 hari praktik" bisa tercetak untuk dokter yang sebenarnya
     hanya praktik dua hari. */
  const jumlahHari = new Set(jadwal.map((j) => j.hari)).size;
  const ringkasHari =
    lang === "en"
      ? `${jumlahHari} practice ${jumlahHari === 1 ? "day" : "days"} per week`
      : `${jumlahHari} hari praktik per minggu`;

  return (
    <article className="overflow-hidden border border-[var(--c-line)] bg-[var(--c-surface)] transition-colors hover:border-[var(--c-brand-line)]" style={{ borderRadius: 14 }}>

      {/* ============================================================
          BARIS KEPALA - foto, identitas, tombol daftar

          TOMBOL DAFTAR ADA DI SINI, bukan di pita terpisah di dasar
          kartu. Dua alasan:

          1. Pita di dasar memakan satu baris penuh hanya untuk satu
             tombol, dan tinggi itu dikalikan puluhan kartu.
          2. Tindakan yang ditawarkan - mendaftar ke dokter ini -
             berkaitan dengan namanya, bukan dengan jadwalnya. Di
             sebelah nama, hubungan itu terbaca sendiri.

          Di ponsel tombolnya turun ke bawah dan melebar penuh: 44px
          tinggi sasaran sentuh, dan sasaran sentuh di pojok kanan
          atas sulit dijangkau ibu jari.
          ============================================================ */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start" style={{ padding: "18px" }}>

        <div style={{ display: "flex", gap: 16, flex: "1 1 auto", minWidth: 0 }}>
          {/* Dokter tanpa foto menampilkan inisial di atas bidang
             berwarna, bukan bingkai abu kosong yang terlihat seperti
             gambar gagal dimuat. */}
          {dokter.foto ? (
            <div style={{ position: "relative", flex: "0 0 auto", width: 88, height: 88, borderRadius: 12, overflow: "hidden", backgroundColor: "var(--c-tint-2)" }}>
              <Image
                src={dokter.foto}
                alt={dokter.nama}
                fill
                sizes="88px"
                style={{ objectFit: "cover", objectPosition: "center top" }}
              />
            </div>
          ) : (
            <div
              aria-hidden="true"
              className="font-bold text-[var(--c-brand)]"
              style={{ flex: "0 0 auto", width: 88, height: 88, borderRadius: 12, backgroundColor: "var(--c-tint-2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 25 }}
            >
              {inisial(dokter.nama)}
            </div>
          )}

          <div style={{ minWidth: 0, flex: "1 1 auto" }}>
            <h3 className="font-bold leading-snug text-[var(--c-ink)]" style={{ fontSize: 18 }}>
              {dokter.nama}
            </h3>

            {/* Spesialisasi jadi lencana, bukan teks abu biasa.

               Di daftar puluhan kartu, spesialisasi adalah hal kedua
               yang dicari mata setelah nama. Sebagai teks abu ia punya
               bobot yang sama dengan keterangan lain di sekitarnya;
               sebagai lencana ia langsung terpisah. */}
            <p style={{ marginTop: 7 }}>
              <span className="border border-[var(--c-brand-line)] bg-[var(--c-tint)] font-semibold text-[var(--c-brand-mid)]" style={{ display: "inline-block", borderRadius: 999, padding: "3px 10px", fontSize: 12 }}>
                {pilihTeks(dokter.spesialisasi, lang)}
              </span>
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginTop: 10 }}>
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={tampilkanJadwal}
                aria-controls={idJadwal}
                className="inline-flex items-center gap-1.5 font-bold text-[var(--c-brand)] transition-colors hover:text-[var(--c-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
                style={{ fontSize: 14 }}
              >
                {tampilkanJadwal ? pilihTeks(TEKS.tutupJadwal, lang) : pilihTeks(TEKS.lihatJadwal, lang)}
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="transition-transform duration-300 motion-reduce:transition-none" style={{ width: 15, height: 15, transform: tampilkanJadwal ? "rotate(180deg)" : "none" }}>
                  <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {jumlahHari > 0 && (
                <>
                  <span aria-hidden="true" style={{ width: 1, height: 12, backgroundColor: "var(--c-line)" }} />
                  <span className="text-[var(--c-muted)]" style={{ fontSize: 13 }}>
                    {ringkasHari}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ⚠️ <button>, BUKAN <a href> LAGI.

            Elemen ini tidak lagi menuju ke mana pun saat ditekan - ia
            membuka dialog. Membiarkannya sebagai <a> berarti klik
            kanan menawarkan "Buka di tab baru", dan tab itu akan
            melompat ke WhatsApp tanpa pernah menampilkan
            peringatannya. */}
        <button
          type="button"
          onClick={(e) => onDaftar({ nama: dokter.nama, href }, e.currentTarget)}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-[10px] bg-[var(--c-brand)] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
          style={{ padding: "10px 18px", fontSize: 13.5 }}
        >
          <IkonWhatsApp />
          {pilihTeks(TEKS.daftar, lang)}
        </button>
      </div>

      {/* ============================================================
          PANEL JADWAL - KISI, BUKAN TABEL SATU KOLOM

          Tiap hari jadi sel tersendiri, dan sel-selnya mengisi lebar
          kartu - empat kolom di layar penuh, dua di tablet, satu di
          ponsel. Ruang terpakai, dan enam hari terbaca sekali pandang
          alih-alih ditelusuri ke bawah.

          HARI DI ATAS, JAM DI BAWAH - bukan berdampingan. Di dalam
          sel selebar 190px, menaruhnya berdampingan mengembalikan
          persoalan jarak yang sama. Bertumpuk, keduanya selalu
          menempel berapa pun lebar selnya.
          ============================================================ */}
      <div id={idJadwal} hidden={!tampilkanJadwal} className="border-t border-[var(--c-line)] bg-[var(--c-surface-2)]" style={{ padding: "14px 18px 16px" }}>

        {/* ============================================================
            PERINGATAN WAJIB JANJI TEMU

            DI DALAM PANEL JADWAL, bukan di baris kepala. Tempatnya
            disengaja: orang yang membuka jadwal sedang memilih hari
            kedatangan - itulah saat aturan "harus janji dulu" berarti
            sesuatu baginya. Di baris kepala, ia terbaca sebelum ada
            rencana yang perlu diubah, dan akan dilewati.

            role="note", BUKAN role="alert". alert memotong pembacaan
            pembaca layar seketika, dan itu untuk hal mendesak seperti
            galat. Ini informasi penting yang tidak mendesak.

            Tombolnya memakai `href` yang SAMA dengan tombol Daftar di
            baris kepala, dan juga lewat dialog yang sama. Kalau hanya
            salah satunya diberi dialog, ada dua jalan ke WhatsApp dari
            satu kartu dan hanya satu yang memperingatkan.
            ============================================================ */}
        {wajibJanji && (
          <div
            role="note"
            className="border border-[var(--c-brand-line)] bg-[var(--c-tint)]"
            style={{ borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span aria-hidden="true" className="text-[var(--c-brand-mid)]" style={{ flex: "0 0 auto", marginTop: 1 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v4M12 17h.01M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                </svg>
              </span>

              <div style={{ minWidth: 0 }}>
                <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 12.5, lineHeight: 1.35 }}>
                  {pilihTeks(TEKS.wajibJanjiJudul, lang)}
                </p>
                <p className="text-[var(--c-body)]" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 3 }}>
                  {pilihTeks(TEKS.wajibJanjiIsi, lang)}
                </p>

                {/* Keterangan "(terbuka di tab baru)" DICABUT dari sini:
                    tombol ini membuka dialog, bukan tab baru. */}
                <button
                  type="button"
                  onClick={(e) => onDaftar({ nama: dokter.nama, href }, e.currentTarget)}
                  className="font-bold text-[var(--c-brand-text)] transition hover:text-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, marginTop: 7 }}
                >
                  <IkonWhatsApp />
                  {pilihTeks(TEKS.wajibJanjiTombol, lang)}
                </button>
              </div>
            </div>
          </div>
        )}

        <p className="font-bold uppercase text-[var(--c-muted)]" style={{ fontSize: 10.5, letterSpacing: "0.09em", marginBottom: 10 }}>
          {pilihTeks(TEKS.jadwalPraktik, lang)}
        </p>

        <ul style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
          {jadwal.map((j, i) => {
            const disorot = hariTerpilih !== "" && String(j.hari) === hariTerpilih;

            return (
              <li
                key={i}
                className={
                  disorot
                    ? "border border-[var(--c-brand)] bg-[var(--c-tint)]"
                    : "border border-[var(--c-line)] bg-[var(--c-surface)]"
                }
                style={{ borderRadius: 10, padding: "9px 12px" }}
              >
                <p className={disorot ? "font-bold text-[var(--c-brand-mid)]" : "font-semibold text-[var(--c-muted)]"} style={{ fontSize: 11.5 }}>
                  {namaHari[j.hari]}
                </p>

                {/* tabular-nums menyamakan lebar tiap angka, jadi
                   jam di sel yang bersebelahan berbaris lurus. */}
                <p className="tabular-nums font-bold text-[var(--c-ink)]" style={{ marginTop: 2, fontSize: 14.5, whiteSpace: "nowrap" }}>
                  {jamTampil(j.mulai, j.selesai)}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </article>
  );
}

/* ============================================================
   DIALOG SEBELUM MENUJU WHATSAPP

   ------------------------------------------------------------
   ⚠️ z-index 9999 DITULIS SEBAGAI INLINE STYLE

   Saya tidak tahu z-index header situs Anda - kalau dialog ini
   berada di bawahnya, header akan menembus lapisan gelapnya dan
   tetap bisa diklik. Angka besar lewat inline style menghindari itu
   tanpa perlu saya tebak.

   ------------------------------------------------------------
   YANG DIKERJAKAN SELAIN MENAMPILKAN KOTAK

   - Escape menutup. Orang menekan Escape untuk keluar dari keadaan
     apa pun yang terasa seperti pertanyaan.
   - Klik di luar kotak menutup. Sama alasannya.
   - Tombol silang di pojok. Escape dan klik di luar sudah menutup,
     tapi keduanya TIDAK TERLIHAT - dan sebagian orang mencari tanda
     silang sebelum mencari tombol "Nanti saja".
   - Fokus pindah ke tombol utama saat terbuka, dan kembali ke tombol
     Daftar yang tadi ditekan saat ditutup.
   - Gulir halaman dikunci selama terbuka. Tanpa ini, menggulir di
     ponsel menggerakkan daftar di belakang dialognya, dan orang
     kehilangan tempatnya di daftar puluhan dokter.

   ⚠️ FOKUS TIDAK DIJEBAK DI DALAM DIALOG. Menekan Tab berulang kali
   akan keluar ke halaman di belakangnya. Untuk dialog dua tombol
   yang mudah ditutup dengan Escape, saya menilai itu tidak sepadan
   dengan kerumitan penjebak fokus buatan sendiri - tapi kalau nanti
   isinya bertambah, pakai pustaka dialog yang sudah teruji, jangan
   menulis sendiri.
   ============================================================ */
function DialogSatuPesan({ lang, nama, onBatal, onLanjut }) {
  const okeRef = useRef(null);

  useEffect(() => {
    okeRef.current?.focus();

    const tekan = (e) => {
      if (e.key === "Escape") onBatal();
    };
    document.addEventListener("keydown", tekan);

    /* Nilai lamanya disimpan, bukan dikosongkan begitu saja - kalau
       ada bagian lain yang juga mengunci gulir, mengembalikannya ke ""
       akan membuka kunci milik orang lain. */
    const gulirLama = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", tekan);
      document.body.style.overflow = gulirLama;
    };
  }, [onBatal]);

  return (
    <div
      onClick={onBatal}
      style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, backgroundColor: "rgba(6, 24, 15, 0.5)", backdropFilter: "blur(3px)" }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="judul-satu-pesan"
        /* Klik di dalam kotak TIDAK menutup dialognya - tanpa baris
           ini, menyeret untuk menyorot teks akan menutupnya. */
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--c-surface)]"
        style={{ position: "relative", width: "100%", maxWidth: 412, borderRadius: 18, overflow: "hidden", boxShadow: "0 24px 56px -20px rgba(6, 24, 15, 0.55)" }}
      >
        <button
          type="button"
          onClick={onBatal}
          aria-label={pilihTeks(TEKS.dialogTutup, lang)}
          className="text-[var(--c-faint)] transition-colors hover:bg-[var(--c-surface-2)] hover:text-[var(--c-body)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--c-brand)]"
          style={{ position: "absolute", top: 12, right: 12, display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 999 }}
        >
          <IkonTutup />
        </button>

        <div style={{ padding: "22px 22px 18px" }}>
          {/* KEPALA - ikon di SAMPING judul, bukan di atasnya.

              Judul dua baris di atas lingkaran memakan tiga baris ruang
              vertikal untuk satu kalimat. Berdampingan, ikonnya mengisi
              ruang yang tadinya kosong di sebelah kiri.

              ⚠️ IKONNYA JAM, BUKAN WHATSAPP. Versi sebelumnya memakai
              ikon WhatsApp di sini - persis sama dengan ikon di tombol
              hijau tiga baris di bawahnya. Dua ikon identik dalam satu
              kotak sekecil ini tidak menambah apa pun, dan yang di
              kepala justru mengumumkan tujuan yang sudah diumumkan
              tombolnya. Jam menyambung dengan isi pesannya: menunggu
              balasan. */}
          <div style={{ display: "flex", gap: 13, alignItems: "flex-start", paddingRight: 30 }}>
            <span className="bg-[var(--c-tint)] text-[var(--c-brand-mid)]" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto", width: 44, height: 44, borderRadius: 999 }}>
              <IkonJam />
            </span>

            <h2 id="judul-satu-pesan" className="font-bold text-[var(--c-ink)]" style={{ marginTop: 3, fontSize: 17, lineHeight: 1.35 }}>
              {pilihTeks(TEKS.dialogJudul, lang)}
            </h2>
          </div>

          {/* Nama dokter: LATAR NETRAL, bukan hijau.

              Versi sebelumnya memakai --c-tint, jadi ada tiga bidang
              hijau dalam satu kotak - lingkaran ikon, blok ini, dan
              tombol utama. Hijau jadi kehilangan artinya. Sekarang ia
              hanya menandai satu hal: tindakan yang dituju.

              Blok ini satu-satunya isi dialog yang berganti tiap kali
              dibuka, jadi pasien perlu bisa memastikannya sekali lirik
              tanpa membaca kalimat apa pun. */}
          {nama && (
            <div className="border border-[var(--c-line)] bg-[var(--c-surface-2)]" style={{ marginTop: 16, borderRadius: 12, padding: "11px 13px" }}>
              <p className="font-semibold uppercase tracking-[0.08em] text-[var(--c-muted)]" style={{ fontSize: 9.5 }}>
                {pilihTeks(TEKS.dialogUntuk, lang)}
              </p>
              <p className="font-bold text-[var(--c-ink)]" style={{ marginTop: 3, fontSize: 13.5, lineHeight: 1.35 }}>
                {nama}
              </p>
            </div>
          )}

          <p className="text-[var(--c-body)]" style={{ marginTop: 15, fontSize: 13.5, lineHeight: 1.75 }}>
            {pilihTeks(TEKS.dialogIsi, lang)}
          </p>

          {/* Ucapan terima kasih diredupkan. Ia penutup yang sopan,
              bukan keterangan yang harus dibaca - memberinya bobot yang
              sama dengan kalimat sebelumnya membuat keduanya sama-sama
              dilewati.

              Garis pemisahnya DICABUT: pita tombol di bawah sudah punya
              garis sendiri, dan dua garis sejajar berjarak dekat
              memotong kotak sekecil ini jadi terlalu banyak bagian. */}
          <p className="text-[var(--c-muted)]" style={{ marginTop: 12, fontSize: 12, lineHeight: 1.6 }}>
            {pilihTeks(TEKS.dialogTerima, lang)}
          </p>
        </div>

        {/* PITA TOMBOL berlatar sendiri, dipisah garis.

            Tombol yang mengambang di atas latar yang sama dengan
            teksnya membuat kotak ini terbaca sebagai satu bidang
            panjang. Dengan pita, mata langsung tahu di mana isinya
            berakhir dan pilihannya dimulai - pola yang sama dengan
            hampir semua dialog sistem operasi.

            wrap-reverse: di layar sempit tombolnya menumpuk, dan tombol
            utama berada di BAWAH, paling dekat dengan ibu jari. */}
        <div className="border-t border-[var(--c-line)] bg-[var(--c-surface-2)]" style={{ display: "flex", flexWrap: "wrap-reverse", gap: 10, padding: "14px 22px 16px" }}>
          <button
            type="button"
            onClick={onBatal}
            className="border border-[var(--c-line)] bg-[var(--c-surface)] font-bold text-[var(--c-body)] transition-colors hover:border-[var(--c-brand)] hover:text-[var(--c-brand-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
            style={{ flex: "1 1 110px", borderRadius: 10, padding: "12px 18px", fontSize: 13.5 }}
          >
            {pilihTeks(TEKS.dialogBatal, lang)}
          </button>

          {/* Tombolnya menyebut tujuannya, bukan cuma "Oke". Tombol yang
              memindahkan orang ke aplikasi lain harus mengatakan ke
              mana - apalagi kalau aplikasinya terbuka di tab baru dan
              halaman ini tetap tertinggal di belakang. */}
          <button
            ref={okeRef}
            type="button"
            onClick={onLanjut}
            className="inline-flex items-center justify-center gap-2 bg-[var(--c-brand)] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
            style={{ flex: "2 1 190px", borderRadius: 10, padding: "12px 18px", fontSize: 13.5, boxShadow: "0 6px 16px -8px rgba(15, 106, 59, 0.8)" }}
          >
            <IkonWhatsApp ukuran={17} />
            {pilihTeks(TEKS.dialogOke, lang)}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PAGINASI

   Dipakai <nav>, bukan <div>, supaya pembaca layar bisa melompat ke
   sini lewat daftar landmark - sama seperti navigasi utama.

   Tombol, bukan <a href="?page=2">. Halaman ini menyimpan penyaring
   di state, bukan di URL (lihat catatan useSearchParams di atas),
   jadi tautan ?page=2 akan memuat ulang halaman dan menghanguskan
   semua centang spesialisasi yang barusan dipilih pengunjung.

   ⚠️ KONSEKUENSINYA, DAN INI DISENGAJA: halaman tidak ikut tersimpan
   di URL. Pengunjung di halaman 4 tidak bisa membagikan tautannya,
   dan tombol Back peramban keluar dari halaman jadwal alih-alih
   kembali ke halaman 3. Untuk daftar dokter yang punya kotak
   pencarian nama, ini tukar-tambah yang wajar - orang mencari nama,
   bukan menghafal nomor halaman. Kalau nanti ternyata salah, yang
   perlu diubah adalah menyimpan SEMUA penyaring di URL sekaligus,
   bukan hanya nomor halamannya.
   ============================================================ */
function nomorHalaman(sekarang, total) {
  /* Sampai 7 halaman, semua nomor muat tanpa berdesakan. Di atas itu
     barulah ditengahi elipsis, supaya barisnya tidak melebar terus. */
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const kiri = Math.max(2, sekarang - 1);
  const kanan = Math.min(total - 1, sekarang + 1);

  const keluar = [1];
  if (kiri > 2) keluar.push("...");
  for (let i = kiri; i <= kanan; i += 1) keluar.push(i);
  if (kanan < total - 1) keluar.push("...");
  keluar.push(total);

  return keluar;
}

function Paginasi({ halaman, total, lang, onPindah }) {
  /* Satu halaman berarti tidak ada yang bisa dituju. Menampilkan
     tombol "1" yang mati hanya menambah benda di layar. */
  if (total <= 1) return null;

  /* ⚠️ text-white, BUKAN text-[var(--c-brand-text)].

     --c-brand-text adalah hijau gelap: warna TEKS DI ATAS LATAR
     TERANG, dipakai pada tautan di blok Wajib Janji dan tombol
     Batal di dialog. Memakainya di atas latar --c-brand
     menghasilkan hijau di atas hijau - tombolnya terlihat kosong.

     Pola yang benar sudah ada di dua tempat lain di berkas ini:
     tombol Daftar dan tombol Lanjut ke WhatsApp, keduanya
     bg-[var(--c-brand)] dengan text-white. */
  const KELAS_DASAR =
    "inline-flex items-center justify-center border font-semibold " +
    "transition-colors focus-visible:outline focus-visible:outline-2 " +
    "focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]";

  const KELAS_AKTIF = "border-[var(--c-brand)] bg-[var(--c-brand)] text-white";

  /* Hover dan focus-visible ditulis sebagai kelas Tailwind, bukan
     objek style.

     Bukan soal selera: style sebaris tidak punya :hover maupun
     :focus-visible sama sekali. Versi pertama komponen ini seluruhnya
     memakai style, jadi tombolnya diam saat disentuh tetikus dan tidak
     memberi tanda apa pun saat dijelajahi dengan Tab - sementara
     SETIAP tombol lain di berkas ini punya cincin fokus. */
  const KELAS_NORMAL =
    "border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-body)] " +
    "hover:border-[var(--c-brand-line)] hover:bg-[var(--c-tint)] " +
    "hover:text-[var(--c-brand-text)]";

  const KELAS_MATI =
    "cursor-not-allowed border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-faint)]";

  /* 42px, bukan 38px.

     Tombol di dialog berdiameter 44 dan tombol Daftar setinggi 42.
     Deretan 38px terlihat sebagai elemen kelas dua di halaman yang
     sama - dan di layar sentuh, petak sekecil itu mulai sering
     meleset. */
  const UKURAN = { minWidth: 42, height: 42, padding: "0 12px", borderRadius: 10, fontSize: 14.5 };

  function kelas(aktif, mati) {
    if (aktif) return `${KELAS_DASAR} ${KELAS_AKTIF}`;
    if (mati) return `${KELAS_DASAR} ${KELAS_MATI}`;
    return `${KELAS_DASAR} ${KELAS_NORMAL}`;
  }

  const diAwal = halaman === 1;
  const diAkhir = halaman === total;

  return (
    <nav
      aria-label={pilihTeks(TEKS.navHalaman, lang)}
      className="border-t border-[var(--c-line)]"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: 32,
        paddingTop: 24,
      }}
    >
      <button
        type="button"
        onClick={() => onPindah(halaman - 1)}
        disabled={diAwal}
        aria-label={pilihTeks(TEKS.halamanSebelumnya, lang)}
        className={kelas(false, diAwal)}
        style={{ ...UKURAN, opacity: diAwal ? 0.45 : 1 }}
      >
        <IkonPanah arah="kiri" />
      </button>

      {nomorHalaman(halaman, total).map((n, i) =>
        n === "..." ? (
          /* aria-hidden: elipsis ini hiasan visual. Dibacakan, ia
             hanya jadi "titik titik titik" di tengah deretan angka. */
          <span
            key={`sela-${i}`}
            aria-hidden="true"
            className="text-[var(--c-faint)]"
            style={{ width: 20, textAlign: "center", fontSize: 14.5, letterSpacing: 1 }}
          >
            &#8230;
          </span>
        ) : (
          <button
            key={n}
            type="button"
            onClick={() => onPindah(n)}
            aria-current={n === halaman ? "page" : undefined}
            aria-label={`${pilihTeks(TEKS.keHalaman, lang)} ${n}`}
            className={kelas(n === halaman, false)}
            style={UKURAN}
          >
            {n}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onPindah(halaman + 1)}
        disabled={diAkhir}
        aria-label={pilihTeks(TEKS.halamanBerikutnya, lang)}
        className={kelas(false, diAkhir)}
        style={{ ...UKURAN, opacity: diAkhir ? 0.45 : 1 }}
      >
        <IkonPanah arah="kanan" />
      </button>
    </nav>
  );
}

/* Panah digambar sebagai SVG, bukan karakter &lsaquo; / &rsaquo;.

   Karakter itu bentuk dan tebalnya ikut jenis huruf yang kebetulan
   terpakai, dan di beberapa jenis huruf ia menggantung tidak rata di
   tengah tombol. Ikon lain di berkas ini - jam, cari, WhatsApp,
   tutup - semuanya sudah SVG; ini menyamakannya. */
function IkonPanah({ arah }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {arah === "kiri" ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
}

function IkonCari() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", width: 17, height: 17, color: "var(--c-faint)" }}
    >
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Kosong({ judul, petunjuk }) {
  return (
    <div
      className="border-2 border-dashed border-[var(--c-brand-line)] bg-[var(--c-tint)]"
      style={{ marginTop: 24, borderRadius: 14, padding: "48px 20px", textAlign: "center" }}
    >
      <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 16 }}>
        {judul}
      </p>
      <p className="text-[var(--c-body)]" style={{ marginTop: 8, maxWidth: 460, marginLeft: "auto", marginRight: "auto", fontSize: 14, lineHeight: 1.65 }}>
        {petunjuk}
      </p>
    </div>
  );
}

/* `ukuran` boleh dikosongkan - bawaannya 16px, jadi semua pemanggilan
   lama di berkas ini tidak perlu diubah. */
function IkonWhatsApp({ ukuran = 16 }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: ukuran, height: ukuran, flex: "0 0 auto" }}>
      <path d="M3.5 20.5l1.3-4.2A8.2 8.2 0 1 1 8 19.3l-4.5 1.2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 8.5c.3 2.5 2 4.2 4.5 4.5l.8-1.3 2 .8v1.3c0 .6-.5 1.1-1.1 1a7.8 7.8 0 0 1-7.2-7.2c0-.6.4-1.1 1-1.1h1.3l.8 2L9 8.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

/* Ikon jam untuk kepala dialog - lihat alasannya di DialogSatuPesan. */
function IkonJam() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 21, height: 21 }}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 7.5V12l3 1.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IkonTutup() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 16, height: 16 }}>
      <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
